// useWorkspace — the workspace read surface (contract 07 §9, ratified R3). Exposes the list +
// current + `workspaceId` (the `<RouterView :key>` source), `currency` (an ISO-4217 CODE — symbol
// selection is `useFormat`'s job, 04), `checkFlag`, `attributes`, and `getAttribute(key)` (the
// `language`/`currency` lookup the boot locale-sync reads). Per R3 it does NOT promise
// `getScheduleByReportId` — schedules/dropdownables belong to reports (Module 08), not here.
//
// `selectWorkspace(ws)` (ORP-18636) is the switch half: it DELEGATES to `SelectWorkspaceFlow`
// (access check → cancel in-flight → reset the workspace scope → re-hydrate → finalize) and returns
// that flow's outcome so the CALLER owns the copy — a flow never toasts and never resolves i18n.
import { computed, type ComputedRef } from 'vue'

import { refreshAttributes } from '../flows/refreshAttributes'
import { SelectWorkspaceFlow, type WorkspaceSwitchOutcome } from '../flows/SelectWorkspaceFlow'
import type { WorkspaceItem } from '../models/workspace.types'
import { useWorkspaceStore, type WorkspaceAttribute } from '../stores/workspaceStore'

export interface UseWorkspace {
  workspaces: ComputedRef<WorkspaceItem[]>
  workspace: ComputedRef<WorkspaceItem | null>
  /** The selected workspace id — the `<RouterView :key="workspaceId">` remount source (05 §7). */
  workspaceId: ComputedRef<WorkspaceItem['id'] | undefined>
  /** ISO-4217 currency CODE (e.g. `'USD'`), or `undefined`. Formatting goes through `useFormat`. */
  currency: ComputedRef<string | undefined>
  /** The raw workspace attribute list (R3). */
  attributes: ComputedRef<WorkspaceAttribute[]>
  /** Feature-flag gate for nav/route visibility (R3; shared shape with `useFeatureFlags`). */
  checkFlag: (key: string) => boolean
  /** The attribute entry for `key` (e.g. `'currency'`, `'language'`), or `null`. */
  getAttribute: (key: string) => WorkspaceAttribute | null
  /** Re-read the selected workspace's attribute blob after a write (**CM-22-01**) — the seam 22's
   *  `SaveAttributeFlow` and all of 30 depend on. Resolves `false` when the re-read failed; the
   *  caller keeps its optimistic value and owns the Retry copy (22 §14). Serialised: a burst of
   *  concurrent writes costs at most two requests, never one per write. */
  refreshAttributes: () => Promise<boolean>
  /** Switch workspace (contract §9 → `SelectWorkspaceFlow`). Re-selecting the current workspace
   *  finalises only (zero network); a target the user cannot access mutates nothing. `status`
   *  stays `ready` throughout — a switch is not a re-boot (contract §7). */
  selectWorkspace: (ws: WorkspaceItem | null) => Promise<WorkspaceSwitchOutcome>
}

export function useWorkspace(): UseWorkspace {
  const workspaceStore = useWorkspaceStore()

  return {
    workspaces: computed(() => workspaceStore.workspaces),
    workspace: computed(() => workspaceStore.workspace),
    workspaceId: computed(() => workspaceStore.workspaceId),
    currency: computed(() => workspaceStore.currency),
    attributes: computed(() => workspaceStore.attributes),
    checkFlag: (key) => workspaceStore.checkFlag(key),
    getAttribute: (key) => workspaceStore.getWorkspaceAttributesByKey(key),
    refreshAttributes: () => refreshAttributes(),
    // Constructed per call, not per `useWorkspace()`: the flow is a one-shot command object.
    selectWorkspace: (ws) => new SelectWorkspaceFlow().execute(ws),
  }
}
